import React, { useEffect, useMemo, useState } from "react";
import TableHeaderInfo from "./components/TableHeaderInfo";
import InstanceTableHeader from "./components/InstanceTableHeader";
import InstanceRow from "./components/InstanceRow";
import SkeletonRow from "./components/SkeletonRow";
import ComparisonRow from "./components/ComparisonRow";
import TotalsRow from "./components/TotalsRow";
import {
  fetchAllInstances,
  fetchSimilarInstances,
} from "../../api/instanceApi";
import {
  compareInstances,
  convertPricesByUnit,
  findSimilarInstances,
  formatSimilarInstancesByProvider,
} from "../../utils/utils";
import { useInstanceStore } from "../../store/useInstanceStore";
import { useFilterStore } from "../../store/filterStore";
import type { Instance } from "../table/types";
import { categoryMapping } from "../../utils/constants";

export const InstanceTable: React.FC = () => {
  const {
    data,
    selectedIds,
    openDropdown,
    showSkeleton,
    page,
    limit,
    setData,
    filterData,
    setFilterData,
    setShowSkeleton,
    toggleSelectAll,
    toggleRowSelect,
    toggleDropdown,
    providerType,
    setSelectedIds,
  } = useInstanceStore();

  const { region, pricingModel, operatingSystem, timeUnit, category } =
    useFilterStore((state) => state);
  const [prevTimeUnit, setPrevTimeUnit] = useState("Per Hour");

  useEffect(() => {
    setPrevTimeUnit(timeUnit);
  }, [timeUnit]);

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [similarData, setSimilarData] = useState<any[]>([]);
  const [selectedInstances, setSelectedInstances] = useState<Record<string, Instance>>({});

  useEffect(() => {
    if (!region || !pricingModel || !providerType || !operatingSystem) return;

    let isCancelled = false;

    const loadData = async () => {
      try {
        setShowSkeleton(true);
        setSelectedIds(new Set());
        const { awsInstances, gcpInstances, azureInstances } =
          await fetchAllInstances(
            providerType,
            page,
            limit,
            region,
            pricingModel,
            operatingSystem
          );

        if (isCancelled) return;

        // ✅ Compute comparable data only once
        const finalData = compareInstances(
          awsInstances,
          gcpInstances,
          azureInstances,
          providerType
        );

        setData(finalData);
        setFilterData(finalData);
      } catch (error) {
        if (!isCancelled)
          console.error(`Error loading ${providerType} instances:`, error);
      } finally {
        if (!isCancelled) setShowSkeleton(false);
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  }, [page, limit, providerType, region, pricingModel, operatingSystem]);

  const handleInstanceClick = (index: number, instance: Instance) => {
    const { setLoadingSimilar, awsInstances, gcpInstances, azureInstances } =
      useInstanceStore.getState();

    toggleDropdown(index);

    setSelectedInstances((prev) => ({
      ...prev,
      [instance.id]: instance,
    }));

    try {
      setLoadingSimilar(true);
      const results = findSimilarInstances(
        instance,
        awsInstances,
        gcpInstances,
        azureInstances,
        providerType
      );
      setSimilarData(results);
    } catch (error) {
      console.error("Error comparing instances:", error);
      setSimilarData([]);
    } finally {
      setLoadingSimilar(false);
    }
  };

  const handleInstanceSelect = (instanceId: string, selectedInstance: Instance) => {
    const isValidPrice = (price: number | string | undefined): boolean => {
      if (price === undefined || price === null) return false;
      if (price === "-") return false;
      if (typeof price === "string" && isNaN(parseFloat(price))) return false;
      return true;
    };

    const currentSelected = selectedInstances[instanceId];
    const baseInstance = currentSelected || data.find((inst: Instance) => inst.id === instanceId);

    if (!baseInstance) return;

    const mergedInstance: Instance = {
      ...baseInstance,
      aws: isValidPrice(selectedInstance.aws) ? selectedInstance.aws : baseInstance.aws,
      gcp: isValidPrice(selectedInstance.gcp) ? selectedInstance.gcp : baseInstance.gcp,
      azure: isValidPrice(selectedInstance.azure) ? selectedInstance.azure : baseInstance.azure,
    };

    setSelectedInstances((prev) => ({
      ...prev,
      [instanceId]: mergedInstance,
    }));

    const currentData = data;
    const updatedData = [...currentData];
    const dataIndex = currentData.findIndex((inst: Instance) => inst.id === instanceId);
    
    if (dataIndex !== -1) {
      updatedData[dataIndex] = {
        ...currentData[dataIndex],
        aws: mergedInstance.aws,
        gcp: mergedInstance.gcp,
        azure: mergedInstance.azure,
      };
      setData(updatedData);
    }

    const currentFilterData = filterData;
    const updatedFilterData = [...currentFilterData];
    const filterIndex = currentFilterData.findIndex((inst: Instance) => inst.id === instanceId);
    if (filterIndex !== -1) {
      updatedFilterData[filterIndex] = {
        ...currentFilterData[filterIndex],
        aws: mergedInstance.aws,
        gcp: mergedInstance.gcp,
        azure: mergedInstance.azure,
      };
      setFilterData(updatedFilterData);
    }
  };

  const handleSort = (key: string) => {
    toggleDropdown(-1);
    if (sortKey === key) {
      setSortOrder(
        sortOrder === "asc" ? "desc" : sortOrder === "desc" ? null : "asc"
      );
      if (sortOrder === "desc") setSortKey(null);
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!sortKey || !sortOrder) return filterData;

    return [...filterData].sort((a, b) => {
      const valA = a[sortKey as keyof Instance];
      const valB = b[sortKey as keyof Instance];

      const numA = Number(valA);
      const numB = Number(valB);

      if (!isNaN(numA) && !isNaN(numB)) {
        return sortOrder === "asc" ? numA - numB : numB - numA;
      }

      return sortOrder === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }, [filterData, sortKey, sortOrder]);

  const categoryKeyMap: Record<string, keyof typeof categoryMapping> = {
    "All Types": "generalPurpose", // Will be handled separately
    "General Purpose": "generalPurpose",
    "Compute Purpose": "computePurpose",
    "Memory Purpose": "memoryPurpose",
  };

  const categoryFilteredData = useMemo(() => {
    if (category === "All Types") {
      return sortedData;
    }
    const categoryKey = categoryKeyMap[category];
    if (!categoryKey || !categoryMapping[categoryKey]) {
      return sortedData;
    }

    const categoryConfig = categoryMapping[categoryKey];
    const prefixes = categoryConfig[providerType] || [];

    if (prefixes.length === 0) {
      return sortedData;
    }

    return sortedData.filter((instance) => {
      const instanceType = instance.instanceType.toLowerCase();
      
      if (providerType === "azure") {
        const normalizedType = instanceType.replace(/^standard_/i, "");
        return prefixes.some((prefix) => {
          const lowerPrefix = prefix.toLowerCase();
          return (
            normalizedType.startsWith(lowerPrefix) ||
            instanceType.includes(lowerPrefix)
          );
        });
      }
      
      return prefixes.some((prefix) =>
        instanceType.startsWith(prefix.toLowerCase())
      );
    });
  }, [sortedData, category, providerType]);
  useEffect(() => {
    if (!filterData.length || !timeUnit || !prevTimeUnit) return;

    const updated = convertPricesByUnit(filterData, prevTimeUnit, timeUnit);

    setFilterData(updated);
  }, [timeUnit]);

  // Calculate selected instances for totals
  const selectedInstancesForTotals = useMemo(() => {
    return categoryFilteredData.filter((instance) => {
      const displayInstance = selectedInstances[instance.id] || instance;
      return selectedIds.has(instance.id);
    }).map((instance) => {
      return selectedInstances[instance.id] || instance;
    });
  }, [categoryFilteredData, selectedIds, selectedInstances]);

  return (
    <div className="bg-white rounded-lg shadow-sm mt-6 relative">
      <TableHeaderInfo
        loading={showSkeleton}
        total={categoryFilteredData.length}
        selected={selectedIds.size}
        onSelectAll={toggleSelectAll}
      />

      <div className="overflow-x-auto relative">
        <table className="min-w-full text-sm relative">
          <InstanceTableHeader
            sortKey={sortKey}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          {!showSkeleton && <TotalsRow selectedInstances={selectedInstancesForTotals} />}
          <tbody className="divide-y divide-gray-200 relative">
            {showSkeleton
              ? Array.from({ length: limit }).map((_, idx) => (
                <SkeletonRow key={idx} />
              ))
              : categoryFilteredData.map((instance, index) => {
                  const displayInstance = selectedInstances[instance.id] || instance;
                  return (
                    <React.Fragment key={instance.id}>
                      <InstanceRow
                        instance={displayInstance}
                        index={index}
                        isSelected={selectedIds.has(instance.id)}
                        onSelect={toggleRowSelect}
                        onToggleDropdown={() =>
                          handleInstanceClick(index, instance)
                        }
                      />
                      <ComparisonRow
                        isOpen={openDropdown === index}
                        instance={displayInstance}
                        originalInstance={instance}
                        similarData={similarData}
                        selectedInstance={selectedInstances[instance.id]}
                        onInstanceSelect={(selectedInstance) =>
                          handleInstanceSelect(instance.id, selectedInstance)
                        }
                      />
                    </React.Fragment>
                  );
                })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InstanceTable;
